import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import SummaryLogo from '@icons/prompt/time-line-summary-icon.svg';
import SummaryLoading from '@icons/prompt/prompt-summary-loading.svg';
import Redo from '@icons/prompt/redo.svg';
import Markdown from '@components/prompt/chat-thread/response-components/Markdown';
import Feedback from '@components/prompt/chat-thread/FeedbackContainer';
import copy from 'copy-to-clipboard';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import {
  AISummaryContainer,
  AISummaryTitle,
  Header,
  AISummaryTitleLeft,
  ResponseContent,
  LoadingContainer,
  ErrorContent,
} from './styles';
import { NexaGateState, useNexaGateCheck } from '@src/components/nexa/utils/nexaGateCheck';
import apptracker from '@src/utils/sensor/apptracker';
import { AI_SUMMARY_GENERATE } from '@src/utils/sensor/track';

interface AISummaryProps {
  isLoading: boolean;
  data: any;
  error: string | null;
  onGenerate: () => Promise<void>;
  onRegenerate: () => Promise<void>;
  disabled?: boolean;
}

export default function AISummary({
  isLoading,
  data,
  error,
  onGenerate,
  onRegenerate,
  disabled = false,
}: AISummaryProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { state: nexaGateState } = useNexaGateCheck();

  // Don't render anything if Nexa is disabled
  if (nexaGateState === NexaGateState.DISABLED) {
    return null;
  }

  const isNexaAllowed = nexaGateState === NexaGateState.ALLOWED;

  const handleGenerateSummary = async () => {
    // If summary already exists, just open the modal
    if (data && !isLoading) {
      setIsModalOpen(true);
      return;
    }

    // Track generate action
    apptracker(AI_SUMMARY_GENERATE, {
      action: 'generate',
      productMode: CURRENT_PRODUCT_MODE,
    });

    setIsModalOpen(true);
    await onGenerate();
  };

  const handleRegenerate = async () => {
    // Track regenerate action
    apptracker(AI_SUMMARY_GENERATE, {
      action: 'regenerate',
      productMode: CURRENT_PRODUCT_MODE,
    });

    await onRegenerate();
  };

  const handleCopy = () => {
    const text = data?.result?.text || data?.result?.summary || '';
    copy(text, { format: 'text/plain' });
    message.info('Copied to clipboard');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Always show button, but disable if Nexa is not allowed or disabled prop is true
  const isAllowed = isNexaAllowed && !disabled;

  const handleTryNexa = () => {
    // Construct Nexa URL based on current product
    const productPath = CURRENT_PRODUCT_MODE === 'EI' ? 'experience-insights' : 'app-insights';
    const nexaUrl = `/app/${productPath}/dashboard/nexa`;
    window.open(nexaUrl, '_blank');
  };

  return (
    <AISummaryContainer>
      <Header>
        <AISummaryTitleLeft>
          <SummaryLogo style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
          <AISummaryTitle>AI Summary</AISummaryTitle>
          <Button
            style={{ marginLeft: '8px' }}
            type="primary"
            ghost
            onClick={handleGenerateSummary}
            loading={isLoading}
            disabled={!isAllowed}
          >
            Generate
          </Button>
          {!isAllowed && (
            <div style={{ marginLeft: '8px' }}>
              <a
                onClick={handleTryNexa}
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px',
                }}
              >
                Please contact your admin to enable Nexa.
              </a>
            </div>
          )}
        </AISummaryTitleLeft>
      </Header>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>{'AI Summary'}</span>
            {data && (
              <Button
                type="primary"
                ghost
                icon={
                  <Redo style={{ fontSize: '12px', marginRight: '8px', verticalAlign: '-1px' }} />
                }
                onClick={handleRegenerate}
                loading={isLoading}
                size="small"
              >
                Regenerate
              </Button>
            )}
          </div>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={
          data && !isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Button icon={<CopyOutlined />} onClick={handleCopy}>
                Copy To Clipboard
              </Button>
              <Feedback
                userMsg={'AI Summary'}
                botMsg={data.result?.text || data.result?.summary || ''}
                timestamp={new Date()}
                useBtn
                style={{
                  display: 'inline-block',
                  margin: 0,
                }}
              />
            </div>
          ) : null
        }
        width="80%"
        style={{ top: 20 }}
        bodyStyle={{
          maxHeight: '80vh',
          overflowY: 'auto',
          padding: '24px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
        }}
      >
        {isLoading && (
          <LoadingContainer>
            <SummaryLoading style={{ width: '32px', height: '32px', margin: 'auto' }} />
            <div>Generating summary...</div>
          </LoadingContainer>
        )}

        {error && !isLoading && (
          <ErrorContent>
            <pre>{error}</pre>
          </ErrorContent>
        )}

        {data && !isLoading && (
          <ResponseContent>
            <Markdown text={data.result?.text || data.result?.summary || 'No summary available'} />
          </ResponseContent>
        )}
      </Modal>
    </AISummaryContainer>
  );
}

// Named export for direct usage
export { AISummary };
